/**
 * Feb-2026 (User-Wunsch 27.05.2026) — Referral-Performance-Mini-Widget.
 *
 * Kompakte Karte, die unter der `TransactionCodeCard` im Dealer-Dashboard
 * angezeigt wird. Zeigt die Performance des persönlichen Transaktions-Codes
 * H_<customer_number> als:
 *
 *   * 3 Mini-KPIs (diese Woche): Besuche · Pageviews · Signups
 *   * 7-Tage-Trend-Sparkline (inline SVG)
 *   * Klick-Link auf die ausführliche Marketing-Statistik-Seite
 *
 * Datenquelle: ``GET /api/marketing/me/performance``.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Users, MousePointer2, UserPlus, ArrowRight, Loader2, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

function Sparkline({ values, height = 26 }) {
    const max = Math.max(1, ...values);
    const w = 120;
    const step = w / Math.max(1, values.length - 1);
    const points = values
        .map((v, i) => `${(i * step).toFixed(1)},${(height - (v / max) * (height - 4) - 2).toFixed(1)}`)
        .join(" ");
    const lastX = ((values.length - 1) * step).toFixed(1);
    const lastY = (height - (values[values.length - 1] / max) * (height - 4) - 2).toFixed(1);
    return (
        <svg viewBox={`0 0 ${w} ${height}`} width={w} height={height} className="overflow-visible" data-testid="referral-sparkline">
            <polyline
                points={points}
                fill="none"
                stroke="#FFCC00"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            <circle cx={lastX} cy={lastY} r="2.6" fill="#111827" />
        </svg>
    );
}

export default function ReferralPerformanceMiniWidget({ user }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user || user.role !== "b2b") return;
        api.get("/marketing/me/performance")
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Performance konnte nicht geladen werden"));
    }, [user]);

    if (!user || user.role !== "b2b") return null;
    if (!user.customer_number) return null;

    if (error) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-3 text-xs text-gray-500" data-testid="referral-mini-error">
                {error}
            </div>
        );
    }
    if (data === null) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-3 text-xs text-gray-500 flex items-center gap-2" data-testid="referral-mini-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Lade Referral-Performance …
            </div>
        );
    }

    const w = data.this_week || {};
    const a = data.all_time || {};
    const trend = data.trend_7d || [0, 0, 0, 0, 0, 0, 0];
    const trendSum = trend.reduce((s, x) => s + x, 0);
    const empty = trendSum === 0 && (w.visits || 0) === 0;

    const refCode = `H_${user.customer_number}`;
    const refLink = `${window.location.origin}/?ref=${encodeURIComponent(refCode)}`;
    const shareLink = async () => {
        try {
            if (navigator.share) {
                await navigator.share({ title: "twowheels", text: "Entdecke meine Fahrzeuge auf twowheels:", url: refLink });
            } else {
                await navigator.clipboard.writeText(refLink);
                toast.success("Empfehlungs-Link kopiert");
            }
        } catch {
            try { await navigator.clipboard.writeText(refLink); toast.success("Empfehlungs-Link kopiert"); } catch { /* ignore */ }
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="referral-performance-widget">
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-[#FFCC00]" />
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600">
                        Referral-Performance
                    </div>
                </div>
                <Link
                    to="/dashboard?tab=marketing"
                    className="text-[10px] uppercase tracking-wider font-bold text-blue-700 hover:underline inline-flex items-center gap-0.5"
                    data-testid="referral-widget-link"
                >
                    Details <ArrowRight className="w-3 h-3" />
                </Link>
            </div>

            {empty ? (
                <div className="py-1.5" data-testid="referral-empty-hint">
                    <div className="text-[11px] font-bold text-gray-700 mb-1">So funktioniert&apos;s</div>
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                        Teilen Sie Ihren persönlichen Empfehlungs-Link. Jeder Klick, Besuch und jede Neuanmeldung über Ihren Code <span className="font-mono-num font-bold text-gray-700">{refCode}</span> wird hier sichtbar.
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={shareLink}
                            className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold text-[11px] uppercase tracking-wider rounded-sm transition-colors"
                            data-testid="referral-share-btn"
                        >
                            <Share2 className="w-3.5 h-3.5" /> Code teilen
                        </button>
                        <button
                            type="button"
                            onClick={async () => { try { await navigator.clipboard.writeText(refLink); toast.success("Link kopiert"); } catch { /* ignore */ } }}
                            className="inline-flex items-center gap-1.5 h-8 px-3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 font-bold text-[11px] uppercase tracking-wider rounded-sm transition-colors"
                            data-testid="referral-copy-btn"
                        >
                            <Copy className="w-3.5 h-3.5" /> Link kopieren
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-2 mb-2.5">
                        <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/40 rounded-sm px-2 py-1.5 text-center" data-testid="referral-week-visits">
                            <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                                <MousePointer2 className="w-2.5 h-2.5" /> Klicks
                            </div>
                            <div className="font-display font-black text-base font-mono-num leading-tight">{w.visits || 0}</div>
                            <div className="text-[8px] text-gray-500">7 Tage</div>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5 text-center" data-testid="referral-week-pageviews">
                            <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                                <Users className="w-2.5 h-2.5" /> Pageviews
                            </div>
                            <div className="font-display font-black text-base font-mono-num leading-tight">{w.pageviews || 0}</div>
                            <div className="text-[8px] text-gray-500">7 Tage</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-sm px-2 py-1.5 text-center" data-testid="referral-week-signups">
                            <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                                <UserPlus className="w-2.5 h-2.5" /> Signups
                            </div>
                            <div className="font-display font-black text-base font-mono-num leading-tight">{w.signups || 0}</div>
                            <div className="text-[8px] text-gray-500">7 Tage</div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 px-1">
                        <Sparkline values={trend} />
                        <div className="text-right">
                            <div className="text-[8px] uppercase tracking-wider font-bold text-gray-500">Gesamt</div>
                            <div className="text-[11px] font-mono-num font-black text-[#111827]" data-testid="referral-all-time-visits">
                                {a.visits || 0} Klicks · {a.signups || 0} Signups
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
